import api from '@/lib/axios';
import { API_BASE } from '@/lib/runtimeConfig';

import type { SessionResponse } from '@/types/session';

/**
 * Fetch current session from Gateway (GET /session, proxied via BFF /api/session).
 * When `sync=true`, appends ?sync=true so the BFF re-fetches live platform data
 * (permissions, org/tenant status) from platform-service and updates Redis before
 * returning. Used on hard refresh — detected via Navigation Timing API in __root.tsx.
 * Entity sessions are excluded from forced sync by the BFF.
 */
export const getSession = async (sync = false): Promise<SessionResponse> => {
  const response = await api.get<SessionResponse>(sync ? '/session?sync=true' : '/session');
  return response.data;
};

/**
 * Logout via BFF (POST /logout).
 * Uses fetch directly — /logout is a BFF-level route, not behind /api gateway.
 * Avoids the axios envelope interceptor which would treat it as an API call.
 *
 * `credentials: 'include'` so the session cookie is sent cross-origin (the BFF
 * lives on a different subdomain in deployed envs). The origin comes from
 * runtimeConfig.API_BASE — derived from window.location.hostname at runtime,
 * empty for same-origin local dev.
 *
 * Throws on non-2xx so callers can surface a failed BFF logout — without this,
 * a silently-failed POST left the SSO cookie intact and the next page render
 * re-authenticated, compounding the "logout took two clicks" symptom.
 */
export const logout = async (): Promise<void> => {
  const response = await fetch(`${API_BASE}/logout`, { method: 'POST', credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Logout failed with status ${response.status}`);
  }
};

export const initiateEntitySwitch = (targetSubdomain: string): void => {
  window.location.href = `${API_BASE}/api/entity-switch?targetSubdomain=${encodeURIComponent(targetSubdomain)}`;
};

export const exitEntity = async (): Promise<string> => {
  const response = await fetch(`${API_BASE}/entity-exit`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) throw new Error(`Entity exit failed: ${response.status}`);
  const data = (await response.json()) as { data?: { redirectUrl?: string } };
  return data.data?.redirectUrl ?? '';
};
